import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faExternalLink, faBell, faPen, faClock } from '@fortawesome/free-solid-svg-icons';
import formattedMoney from '../../utils/formatMoney';

const PLATFORM_BADGE = {
    shopee: { label: 'Shopee', bg: '#FFF2F0', color: '#EE4D2D' },
    lazada: { label: 'Lazada', bg: '#EEEEFF', color: '#0F146B' },
    tiktok: { label: 'TikTok', bg: '#F1F1F1', color: '#010101' },
};

function formatRelativeTime(iso) {
    if (!iso) return 'Chưa kiểm tra';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Vừa xong';
    if (mins < 60) return `${mins} phút trước`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} giờ trước`;
    return `${Math.floor(hrs / 24)} ngày trước`;
}

function getPriceChange(initial, current) {
    if (!initial || initial === 0) return null;
    const diff = ((current - initial) / initial) * 100;
    if (diff < -0.1) return { text: `↓ ${Math.abs(diff).toFixed(0)}%`, colorClass: 'text-green-600', bgClass: 'bg-green-50' };
    if (diff > 0.1) return { text: `↑ ${diff.toFixed(0)}%`, colorClass: 'text-red-500', bgClass: 'bg-red-50' };
    return null;
}

/** Card ngang: ảnh + platform badge + thông tin giá + actions */
function PriceWatchCard({ item, onRemove, onBuy, onEdit }) {
    const change = getPriceChange(item.initialPrice, item.lastKnownPrice);
    const badge = PLATFORM_BADGE[item.platform] || { label: item.platform, bg: '#f5f5f5', color: '#333' };
    const savings = item.initialPrice > 0 ? item.initialPrice - item.lastKnownPrice : 0;

    return (
        <div className="bg-surface rounded-xl shadow-sm p-3 flex gap-3">
            {/* Product image + platform badge */}
            <div className="relative w-18 h-18 flex-shrink-0 rounded-lg overflow-hidden bg-surface-muted" style={{ width: 72, height: 72 }}>
                <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover" />
                <span
                    className="absolute bottom-0 left-0 right-0 text-center py-0.5 font-bold"
                    style={{ fontSize: 8, backgroundColor: badge.bg, color: badge.color }}
                >
                    {badge.label}
                </span>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
                <p className="text-xs font-semibold leading-tight line-clamp-2">{item.productName}</p>

                {/* Current price + change badge + flash sale */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-sm font-bold ${change?.colorClass ?? 'text-content'}`}>
                        {formattedMoney.format(item.lastKnownPrice)}
                    </span>
                    {item.isFlashSale && (
                        <span className="text-[9px] font-bold bg-orange-500 text-white px-1.5 py-0.5 rounded">⚡ FLASH</span>
                    )}
                    {change && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${change.colorClass} ${change.bgClass}`}>
                            {change.text}
                        </span>
                    )}
                </div>

                {/* Original price gạch ngang */}
                {item.originalPrice != null && item.originalPrice > item.lastKnownPrice && (
                    <span className="text-[10px] text-content-3 line-through">
                        {formattedMoney.format(item.originalPrice)}
                    </span>
                )}

                {/* Tiết kiệm vs ban đầu */}
                {savings > 0 && (
                    <span className="text-[10px] text-green-600">
                        Tiết kiệm {formattedMoney.format(savings)} so với ban đầu
                    </span>
                )}

                {/* All-time low */}
                {item.allTimeMin != null && item.allTimeMin > 0 ? (
                    <span className="text-[10px] text-content-3">
                        Thấp nhất mọi thời đại:{' '}
                        <span className="font-semibold text-green-600">{formattedMoney.format(item.allTimeMin)}</span>
                    </span>
                ) : (
                    <span className="text-[10px] text-content-3">
                        Giá thấp nhất:{' '}
                        <span className="font-semibold text-green-600">{formattedMoney.format(item.lowestPrice)}</span>
                    </span>
                )}

                {/* Below avg indicator */}
                {item.avgPrice != null && item.avgPrice > 0 && item.lastKnownPrice < item.avgPrice && (
                    <span className="text-[10px] text-primary-500">
                        Đang thấp hơn giá TB ({formattedMoney.format(item.avgPrice)})
                    </span>
                )}

                {/* Last checked */}
                <div className="flex items-center gap-1">
                    <FontAwesomeIcon icon={faClock} className="text-content-3 text-[9px]" />
                    <span className="text-[10px] text-content-3">Kiểm tra: {formatRelativeTime(item.lastCheckedAt)}</span>
                </div>

                {/* Alert condition */}
                <div className="inline-flex self-start items-center gap-1 bg-primary-50 rounded px-2 py-1">
                    <FontAwesomeIcon icon={faBell} className="text-primary-500 text-[10px] flex-shrink-0" />
                    <span className="text-[10px] text-primary-600">
                        {item.alertTargetPrice != null
                            ? `Dưới ${formattedMoney.format(item.alertTargetPrice)}`
                            : `Giảm ≥ ${item.alertThreshold}%`}
                    </span>
                </div>
            </div>

            {/* Actions column */}
            <div className="flex flex-col justify-between gap-1.5 flex-shrink-0">
                <button
                    onClick={onBuy}
                    className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center hover:bg-primary-100 transition-colors"
                    title="Mở link mua"
                >
                    <FontAwesomeIcon icon={faExternalLink} className="text-primary-500 text-xs" />
                </button>
                <button
                    onClick={onEdit}
                    className="w-8 h-8 rounded-lg bg-yellow-50 flex items-center justify-center hover:bg-yellow-100 transition-colors"
                    title="Chỉnh alert"
                >
                    <FontAwesomeIcon icon={faPen} className="text-yellow-500 text-xs" />
                </button>
                <button
                    onClick={onRemove}
                    className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors"
                    title="Xóa theo dõi"
                >
                    <FontAwesomeIcon icon={faTrash} className="text-red-400 text-xs" />
                </button>
            </div>
        </div>
    );
}

export default PriceWatchCard;
